import React, { useMemo } from 'react';
import type { PriceChangeSubmission } from '@app/model/pricing-automation/PriceChangeSubmission';
import { PriceChangeTooltipSection, priceChangeMarkerSeries, syncPriceChangeMarkerColor, tooltipPointIndex } from './PriceChangeMarkers';
import MarketStateDailySnapshotWithComparisonOutput
	from '@app/model/market_state/output/MarketStateDailySnapshotWithComparisonOutput';
import Highcharts from 'highcharts';
import Utilities from '@util/Utilities';
import moment from 'moment/moment';
import ReactDOMServer from 'react-dom/server';
import _orderBy from 'lodash/orderBy';
import Message from '@app/component/message/Message';
import HighchartsReact from 'highcharts-react-official';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import { MarketStateStatDateSnapshot } from '@app/model/market_state/MarketStateStatDateSnapshot';
import {
	useMarketStateFilterInputTools
} from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import GraphUtilities from '@app/site/secured/rateshopper/rates/dashboard/GraphUtilities';


const MarketSateDayPriceEvolutionAnalyticalGraph: React.FunctionComponent<{ data: MarketStateStatDateSnapshot[]; priceChanges: PriceChangeSubmission[] }>
	= ({ data, priceChanges }) => {

	const { companyHotel } = useSelectedCompanyHotel();

	const { filter } = useMarketStateFilterInputTools();

	Highcharts.SVGRenderer.prototype.symbols.cross = (x: number, y: number, w: number, h: number) => {
		return ['M', x, y, 'L', x + w, y + h, 'M', x + w, y, 'L', x, y + h, 'z'];
	};

	// Build the options only when the inputs change. A new options object makes HighchartsReact call
	// chart.update(), which resets the series that the user turned on or off in the legend.
	const options = useMemo(() => {
		const pointDates = data.map(day => day.latestUpdateDate);


		// @ts-ignore
		const marketStates = data!.map(d => new MarketStateDailySnapshotWithComparisonOutput({
			current: d,
			previous: undefined,
			statDate: d.statDate,
		}));

		const hotelLines = GraphUtilities.sortAllSeriesByAveragePrice(companyHotel, marketStates, true, false, true,
			filter.competitorTagIds, true);
		const markers = priceChangeMarkerSeries(pointDates, GraphUtilities.findMainHotelData(companyHotel, marketStates), priceChanges);

		return {
			chart: {
				type: 'line',
				height: '800',
				events: { render: function(this: any) { syncPriceChangeMarkerColor(this); } },
			},
			title: {
				text: '',
			},
			plotOptions: { series: { states: { inactive: { enabled: false } } } },
			xAxis: {
				categories: marketStates.map((ms, index) => {
					const date = Utilities.formatDateString(ms.current.latestUpdateDate!);
					const now = moment().format('DD/MM/yyyy');
					return now == date ? 'Today' : date;
				}),
				alternateGridColor: '#f3f4f6',
				crosshair: true,
			},
			yAxis: {
				title: {
					text: 'Price',
				},
			},
			tooltip: {
				shared: true,
				useHTML: true,
				// @ts-ignore
				formatter: function() {
					// One tooltip for the day: the line and the price-change dot both show it.
					// @ts-ignore
					const chart = (this.points?.[0] ?? this).series.chart;
					const xValue = tooltipPointIndex(this);
					const points = chart.series
						.filter((series: Highcharts.Series) => series.visible && series.options.id !== 'price-changes')
						.map((series: Highcharts.Series) => ({ series, point: series.data[xValue] as Highcharts.Point | undefined }))
						.filter(({ point }: { point?: Highcharts.Point }) => point !== undefined && point.y !== null && point.y !== undefined);
					return ReactDOMServer.renderToString(<div>
						{formatTooltip(points, chart.xAxis[0].categories[xValue])}
						<PriceChangeTooltipSection days={markers.days} pointIndex={xValue} />
					</div>);
				},
				valueDecimals: 2,
			},
			series: [...hotelLines, ...(markers.series ? [markers.series] : [])],
		};
	}, [data, priceChanges, companyHotel, filter]);

	function formatTooltip(points: any[], category: string) {
		return <table className="min-w-full divide-y divide-gray-300 text-gray-500">
			<thead>
			<tr>
				<th scope="col"
						className="py-2 px-3 text-left text-xs font-semibold text-yellow-900 sm:pl-0" colSpan={3}>
					{category}
				</th>
			</tr>
			</thead>
			<tbody className="divide-y divide-gray-200 bg-white">
			{_orderBy(points, p => p.point.val ? parseFloat(p.point.val) : Number.MIN_VALUE, 'desc').map((point, idx) => <tr
				key={idx}>
				<td className="whitespace-nowrap px-2 py-2 text-xs"
						style={{ color: point.series.color }}>{point.series.name}</td>
				<td className="whitespace-nowrap px-2 py-2 text-xs" style={{ textAlign: 'right' }}>
					<b>{point.point.displayValue}</b>
				</td>
				<td className="whitespace-nowrap px-2 py-2 text-xs" style={{ textAlign: 'left' }}>
					<b>{Utilities.findRoomMappingName(point.point.roomType, companyHotel.roomMappingTypeConfigurations)}</b>
				</td>
				<td className="whitespace-nowrap px-2 py-2 text-xs" style={{ textAlign: 'left' }}>
					<b>{Utilities.trim(point.point.roomName, 20)}</b>
				</td>
			</tr>)}
			</tbody>
		</table>;
	}

	const highcharts = Highcharts;


	return <div style={{ position: 'relative' }}>
		{GraphUtilities.isFilterComputable(filter) &&
			<Message extraClassName="mb-4" state="info">
				The graph is calculated using Booking Web public rates</Message>}
		<HighchartsReact
			highcharts={highcharts}
			options={options}
		/>
	</div>;
};

export default MarketSateDayPriceEvolutionAnalyticalGraph;
